import type { GrammarTopic } from './types';

export const advancedTensesTopics: GrammarTopic[] = [
  {
    slug: 'past-habits-used-to-would',
    title: 'Talking About Past Habits: Used To & Would',
    blocks: [
      {
        kind: 'p',
        text: 'When we talk about things in the past that are not true any more, we can use “used to” or “would”.',
      },
      { kind: 'h3', text: '1. Used to + infinitive' },
      {
        kind: 'p',
        text: 'We can use “used to” to talk about past states that are not true anymore or about repeated past habits that don’t happen anymore. “Used to” can be used with action and state verbs.',
      },
      {
        kind: 'examples',
        items: [
          'We **used to live** in New York when I was a kid.',
          'There **didn\'t use to be** a supermarket here. When did it open?',
          '**Did** you **use to have** a garden?',
          'I **used to/would go** swimming every Thursday when I was at school.',
          'She **used to/would smoke** but she gave up a few years ago.',
        ],
      },
      { kind: 'h3', text: '2. Would + infinitive' },
      {
        kind: 'p',
        text: 'We can use “would” to talk about repeated past actions that don\'t happen anymore. However, “would” is only used with action verbs.',
      },
      {
        kind: 'examples',
        items: [
          'Every Saturday I **would go** on a long bike ride.',
          'My dad **would read** me amazing stories every night at bedtime.',
        ],
      },
      { kind: 'h3', text: 'Would x used to' },
      {
        kind: 'p',
        text: 'There is a slight difference in the use of “would” and “used to”. “Would” for past habits is slightly more formal than “used to” and it is often used in stories, such as in The Missing Piece. We don\'t normally use the negative or question form of “would” for past habits. In addition, we do not use “would” to talk about past states.',
      },
    ],
  },
  {
    slug: 'past-continuous',
    title: 'The Past Continuous',
    blocks: [
      {
        kind: 'p',
        text: 'The past continuous describes actions or events which began in the past and were still going on (in progress) when another event occurred.',
      },
      {
        kind: 'examples',
        items: [
          'They **were waiting** for the bus when the accident happened.',
          'Caroline **was skiing** when she broke her leg.',
          'When we arrived he **was having** a bath.',
          'When the fire started I **was watching** television.',
        ],
      },
      {
        kind: 'h3',
        text: '1. Used to describe the background in a story written in the past tense',
      },
      {
        kind: 'examples',
        items: [
          'The sun **was shining** and the birds **were singing** as the elephant came out of the jungle. The other animals **were relaxing** in the shade of the trees, but the elephant moved very quickly. She **was looking** for her baby, and she didn\'t notice the hunter who **was watching** her through his binoculars. When the shot rang out, she **was running** towards the river...',
        ],
      },
      {
        kind: 'h3',
        text: '2. Used to describe an unfinished action that was interrupted by another event or action',
      },
      {
        kind: 'examples',
        items: ['I **was having** a beautiful dream when the alarm clock rang.'],
      },
      { kind: 'h3', text: '3. Used to express a change of mind' },
      {
        kind: 'examples',
        items: [
          'I **was going to spend** the day at the beach but I\'ve decided to get my homework done instead.',
        ],
      },
      { kind: 'h3', text: '4. Used with \'wonder\', to make a very polite request' },
      {
        kind: 'examples',
        items: ['I **was wondering** if you could babysit for me tonight.'],
      },
    ],
  },
  {
    slug: 'present-perfect',
    title: 'Present Perfect',
    blocks: [
      {
        kind: 'p',
        text: 'The present perfect is formed by the auxiliary verb HAVE or HAS + the past participle of a verb (-ed or 3rd column). We use the present perfect:',
      },
      {
        kind: 'h3',
        text: '1. To talk about something that started in the past and continues being true in the present',
      },
      {
        kind: 'examples',
        items: [
          '**They’ve been** married for fifty years. → They are still married.',
          'She **has lived** in Liverpool all her life. → She still lives there.',
          'I **haven’t played** the piano in years. → And I still haven’t played it.',
        ],
      },
      {
        kind: 'h3',
        text: '2. When we are talking about life experiences and accomplishments up to the present',
      },
      { kind: 'p', text: 'Because they have an impact on your present.' },
      {
        kind: 'examples',
        items: [
          '**I’ve graduated** from uni in Canada. → It is still important to me.',
          '**I’ve been** to Paris three times in my life.',
          'I **have never gone** on a blind date, but I would love to. → In the negative, it implies that we are open to having the experience in the future.',
          '**I’ve never been** bungee jumping. → I’m open to this experience, it’s something I’m planning on doing.',
        ],
      },
      {
        kind: 'note',
        text: 'We often use the adverb ever to talk about experiences up to the present, and we use never for the negative form:',
      },
      {
        kind: 'examples',
        items: [
          'My last birthday was the worst day I **have ever had**.',
          '“**Have** you **ever met** George?” “Yes, but **I\'ve never met** his wife.”',
        ],
      },
      {
        kind: 'h3',
        text: '3. To talk about something that happened in the past and impacts the present',
      },
      {
        kind: 'examples',
        items: [
          'I can\'t get in the house. **I\'ve lost** my keys. → The result of losing my keys is not being able to get in the house.',
          'Teresa isn\'t at home. I think she **has gone** shopping. → The result of Teresa going shopping is the fact that she isn’t at home now.',
          '**Have** you **cooked** bread? → In the present, you feel the smell of bread.',
        ],
      },
      { kind: 'h3', text: 'How do we form the present perfect?' },
      { kind: 'h3', text: 'a. Affirmative form' },
      {
        kind: 'p',
        text: 'HAVE or HAS + Verb in the Participle Form (-ed or third column)',
      },
      { kind: 'examples', items: ['I **have been** to Paris before.'] },
      { kind: 'h3', text: 'b. Negative form' },
      {
        kind: 'p',
        text: 'HAVE NOT (HAVEN’T) or HAS NOT (HASN’T) + Verb in the Participle Form (-ed or third column)',
      },
      { kind: 'examples', items: ['I **haven’t been** to Paris before.'] },
      { kind: 'h3', text: 'c. Interrogative form' },
      {
        kind: 'p',
        text: 'HAVE or HAS + Subject + Verb in the Participle Form (-ed or third column)',
      },
      { kind: 'examples', items: ['**Have** you **been** to Paris before?'] },
      { kind: 'h3', text: 'Irregular verbs chart (complete)' },
      {
        kind: 'table',
        header: ['Infinitive', 'Simple Past', 'Past Participle'],
        rows: [
          ['be', 'was/were', 'been'],
          ['I am', 'I was', 'I have been'],
          ['he is', 'he was', 'he has been'],
          ['we are', 'we were', 'we have been'],
          ['beat', 'beat', 'beaten'],
          ['become', 'became', 'become'],
          ['begin', 'began', 'begun'],
          ['bet*', 'bet', 'bet'],
          ['blow', 'blew', 'blown'],
          ['break', 'broke', 'broken'],
          ['bring', 'brought', 'brought'],
          ['build', 'built', 'built'],
          ['burst', 'burst', 'burst'],
          ['buy', 'bought', 'bought'],
          ['catch', 'caught', 'caught'],
          ['choose', 'chose', 'chosen'],
          ['come', 'came', 'come'],
          ['cost', 'cost', 'cost'],
          ['cut', 'cut', 'cut'],
          ['deal', 'dealt', 'dealt'],
          ['do', 'did', 'done'],
          ['draw', 'drew', 'drawn'],
          ['drink', 'drank', 'drunk'],
          ['drive', 'drove', 'driven'],
          ['eat', 'ate', 'eaten'],
          ['fall', 'fell', 'fallen'],
          ['feed', 'fed', 'fed'],
          ['feel', 'felt', 'felt'],
          ['fight', 'fought', 'fought'],
          ['find', 'found', 'found'],
          ['fly', 'flew', 'flown'],
          ['forget', 'forgot', 'forgotten'],
          ['freeze', 'froze', 'frozen'],
          ['get', 'got', 'got, gotten'],
          ['give', 'gave', 'given'],
          ['go', 'went', 'gone'],
          ['grow', 'grew', 'grown'],
          ['hang', 'hung', 'hung'],
          ['have', 'had', 'had'],
          ['hear', 'heard', 'heard'],
          ['hide', 'hid', 'hidden'],
          ['hit', 'hit', 'hit'],
          ['hold', 'held', 'held'],
          ['hurt', 'hurt', 'hurt'],
          ['keep', 'kept', 'kept'],
          ['know', 'knew', 'known'],
          ['lay', 'laid', 'laid'],
          ['lead', 'led', 'led'],
          ['leave', 'left', 'left'],
          ['lend', 'lent', 'lent'],
          ['let', 'let', 'let'],
          ['light*', 'lit', 'lit'],
          ['lose', 'lost', 'lost'],
          ['make', 'made', 'made'],
          ['mean', 'meant', 'meant'],
          ['meet', 'met', 'met'],
          ['pay', 'paid', 'paid'],
          ['put', 'put', 'put'],
          ['read', 'read', 'read'],
          ['ride', 'rode', 'ridden'],
          ['ring', 'rang', 'rung'],
          ['rise', 'rose', 'risen'],
          ['run', 'ran', 'run'],
          ['say', 'said', 'said'],
          ['see', 'saw', 'seen'],
          ['sell', 'sold', 'sold'],
          ['send', 'sent', 'sent'],
          ['set', 'set', 'set'],
          ['shake', 'shook', 'shaken'],
          ['steal', 'stole', 'stolen'],
          ['shine', 'shone', 'shone'],
          ['shoot', 'shot', 'shot'],
          ['show*', 'showed', 'shown/showed'],
          ['shut', 'shut', 'shut'],
          ['sing', 'sang', 'sung'],
          ['sink', 'sank', 'sunk'],
          ['sit', 'sat', 'sat'],
          ['sleep', 'slept', 'slept'],
          ['slide', 'slid', 'slid'],
          ['speak', 'spoke', 'spoken'],
          ['spend', 'spent', 'spent'],
          ['spring', 'sprang', 'sprung'],
          ['stand', 'stood', 'stood'],
          ['stick', 'stuck', 'stuck'],
          ['swear', 'swore', 'sworn'],
          ['sweep', 'swept', 'swept'],
          ['swim', 'swam', 'swum'],
          ['swing', 'swung', 'swung'],
          ['take', 'took', 'taken'],
          ['teach', 'taught', 'taught'],
          ['Tear (tér)', 'tore', 'torn'],
          ['tell', 'told', 'told'],
          ['think', 'thought', 'thought'],
          ['throw', 'threw', 'thrown'],
          ['understand', 'understood', 'understood'],
          ['wake*', 'woke', 'woken'],
          ['wear', 'wore', 'worn'],
          ['weave', 'wove', 'woven'],
          ['win', 'won', 'won'],
          ['write', 'wrote', 'written'],
        ],
      },
    ],
  },
  {
    slug: 'transcendent-aspect',
    title: 'The Transcendent Aspect (Perfect)',
    blocks: [
      {
        kind: 'p',
        text: 'Also known as “perfect”, the transcendent aspect has this name because it refers to a tense that transcends the moment of the event. When we use the “perfect”, we want to convey that the speaker is in the result phase of an event. For now, let’s focus on the present perfect.',
      },
      {
        kind: 'examples',
        items: [
          '**I’ve lost** my keys. I can’t open the door. → the result of losing your keys is that you cannot open the door.',
          'I **have been** to Paris twice in my life, I love that city. → the result of being in Paris twice is that you love that city.',
        ],
      },
      {
        kind: 'p',
        text: 'The present perfect is used to talk about past situations, just as the simple past. While in the simple past, the focus is on the past event itself, in the present perfect the focus is on the results that this past event has.',
      },
      { kind: 'p', text: 'You’ve been good. vs You were good.' },
      {
        kind: 'p',
        text: 'The transcendent aspect gives the idea that the state of being good surely extends to the present and may extend to the future as well. “You’ve been good, therefore I will take you to play in the park.” → implicit congratulation, proudness.',
      },
      {
        kind: 'p',
        text: 'On the other hand, the simple past means that the state of being good is no longer there, which means that the person is not good anymore.',
      },
      { kind: 'h3', text: 'How do I choose?' },
      {
        kind: 'p',
        text: 'Of course, you do not need to think about it this way when you are speaking.',
      },
      {
        kind: 'p',
        text: 'Usually, we use the simple past when we are telling a story and the focus is not on how the fact makes us feel now but rather simply on what happened.',
      },
      {
        kind: 'p',
        text: 'On the other hand, we use the present perfect when we talk about actions with results or influence in the present or with an unfinished time word (this week, this month, today).',
      },
    ],
  },
  {
    slug: 'past-perfect',
    title: 'Past Perfect',
    blocks: [
      {
        kind: 'p',
        text: 'The past perfect is a verb tense used to talk about actions that were completed before some point in the past. Therefore, we use it to talk about something that happened before something else: it is the past of the past!',
      },
      {
        kind: 'p',
        text: 'To form the past perfect we use the auxiliary verb HAD + the participle form of the main verb.',
      },
      {
        kind: 'examples',
        items: [
          'I was surprised (2) to know that someone **had sent** me a message (1) that day.',
          'We were shocked (2) to discover that someone **had graffitied** “John was here” (1) on our front door. We were relieved (2) that John **had used** washable paint (1).',
          'We looked for witnesses (2), but the neighbors **hadn’t seen** John in the act (1). If John **hadn’t included** (1) his own name in the message, we would have no idea (2) who was behind it.',
        ],
      },
      { kind: 'h3', text: 'Past perfect x simple past' },
      {
        kind: 'p',
        text: 'When you’re talking about some point in the past and want to reference an event that happened even earlier, using the past perfect allows you to convey the sequence of the events. You do not need to use the past perfect if you are not conveying an idea of a sequence of events.',
      },
    ],
  },
  {
    slug: 'present-perfect-continuous',
    title: 'Present Perfect Continuous',
    blocks: [
      {
        kind: 'p',
        text: 'The present perfect continuous shows that something started in the past and is continuing at the present time. The transcendent aspect (perfect) gives the idea that we are in the result phase of an event, and the continuous aspect proves that this phase is still lasting.',
      },
      {
        kind: 'p',
        text: 'The present perfect continuous is formed using the construction has/have been + the gerund (root + -ing).',
      },
      {
        kind: 'examples',
        items: [
          'I **have been reading** War and Peace for a month now. → person still reading',
          'I **have read** War and Peace and I hated it. → reading is not happening',
        ],
      },
      {
        kind: 'p',
        text: 'In this sentence, using the present perfect continuous verb tense conveys that reading War and Peace is an activity that began some time in the past and is not yet finished in the present.',
      },
      {
        kind: 'examples',
        items: [
          'Mia **has been competing** in flute competitions recently. (And she will continue to do so.)',
          'I **haven’t been feeling** well lately. (And I am still sick now.)',
          '**Have** you **been treating** yourself with kindness?',
        ],
      },
      {
        kind: 'note',
        text: '“Recently” and “lately” are words that we often find with verbs in the present perfect continuous tense.',
      },
    ],
  },
  {
    slug: 'zero-first-conditionals',
    title: 'Zero and First Conditionals: Real Possibilities',
    blocks: [
      {
        kind: 'p',
        text: 'Conditional sentences express the consequences of a factual or hypothetical situation. They are usually explained as containing two parts: an if-clause and a consequence clause.',
      },
      { kind: 'h3', text: 'The Zero Conditional' },
      {
        kind: 'p',
        text: 'We can make a zero conditional sentence with two verbs in the present simple (one in the \'if clause\' and one in the \'main clause’).',
      },
      {
        kind: 'p',
        text: 'This conditional is used when the result will always happen. So, if water reaches 100 degrees, it always boils. It\'s a fact. I\'m talking in general, not about one particular situation. The result of the \'if clause\' is always the main clause.',
      },
      {
        kind: 'examples',
        items: [
          'If water **reaches** 100 degrees, it **boils**.',
          'If I **eat** peanuts, I **get** sick.',
          'If you **touch** a fire, you **get burned**.',
          'If someone **sends** you sweets, you **feel** happy.',
        ],
      },
      { kind: 'h3', text: 'The First Conditional' },
      {
        kind: 'p',
        text: 'The first conditional has the present simple after \'if\', then the future simple in the other clause.',
      },
      {
        kind: 'p',
        text: 'It\'s used to talk about things that may happen in the future. Of course, we can\'t know what will happen in the future, but this describes possible things, which could easily come true.',
      },
      {
        kind: 'examples',
        items: [
          'If I **study** today, **I\'ll go** to the party tonight.',
          'If I **have** enough money, **I\'ll buy** some new shoes.',
          'If you **study** hard, you **will pass** the test.',
          'If it **rains**, I **won\'t go** to the park.',
          'If **it\'s** sunny, **we\'ll go** to the park.',
          'If you **cook**, **I\'ll wash** the dishes.',
        ],
      },
      {
        kind: 'note',
        text: 'If the "if" clause comes first, a comma is usually used. If the "if" clause comes second, there is no need for a comma:',
      },
      {
        kind: 'examples',
        items: [
          'You **will pass** the test if you **study** hard.',
          'If you **study** hard, you **will pass** the test.',
        ],
      },
    ],
  },
  {
    slug: 'second-conditional',
    title: 'The Second Conditional: Unreal Situation or Possibility',
    blocks: [
      {
        kind: 'p',
        text: 'Conditional sentences express the consequences of a factual or hypothetical situation. They are usually explained as containing two parts: an if-clause and a consequence clause.',
      },
      { kind: 'h3', text: 'The second conditional' },
      {
        kind: 'p',
        text: 'The second conditional uses the past simple after if, then \'would\' and the infinitive.',
      },
      {
        kind: 'p',
        text: 'First, we can use it to talk about things in the future that are probably not going to be true, like a dream that you have but is unachievable.',
      },
      {
        kind: 'p',
        text: 'Second, we can use it to talk about something in the present which is impossible, because it\'s not true.',
      },
      {
        kind: 'examples',
        items: [
          'If I **met** the King of England, I **would say** hello.',
          'She **would travel** all over the world if she **were** rich.',
          'If I **had** his number, I **would call** him. (I don\'t have his number now, so it\'s impossible for me to call him).',
        ],
      },
      { kind: 'h3', text: 'What is the difference?' },
      {
        kind: 'examples',
        items: [
          'If I **win** the lottery, I **will travel** around the world. = the person has the ticket',
          'Vs. If I **won** the lottery, I **would travel** around the world. = the person doesn’t have the ticket',
        ],
      },
    ],
  },
  {
    slug: 'third-conditional',
    title: 'The Third Conditional: Past Possibilities',
    blocks: [
      {
        kind: 'p',
        text: 'The third conditional is used to talk about things that could have happened a certain way, but did not. It’s the conditional we use to talk about regrets.',
      },
      {
        kind: 'p',
        text: 'We make the third conditional by using the past perfect (had + verb in the participle) after \'if\' and then \'would have\' and the past participle in the second part of the sentence.',
      },
      {
        kind: 'examples',
        items: [
          'If she **had studied**, she **would have passed** the exam → we know she didn\'t study, and so she didn\'t pass.',
          'If I **hadn\'t eaten** so much, I **wouldn\'t have felt** sick → I did eat a lot, so I did feel sick.',
          'If we **had taken** a taxi, we **wouldn\'t have missed** the plane. → We didn’t take a taxi, so we missed the plane.',
          'She **wouldn\'t have been** tired if she **had gone** to bed earlier. → She slept late, so she was tired.',
          'He **would have been** on time for the interview if he **had left** the house at nine. → He didn’t leave at 9, so he wasn’t on time for the interview.',
        ],
      },
      { kind: 'h3', text: 'What’s the difference?' },
      {
        kind: 'list',
        items: [
          '1st Conditional: If I **win** the lottery, I **will travel** around the world.',
          '2nd Conditional: If I **won** the lottery, I **would travel** around the world.',
          '3rd Conditional: If I **had won** the lottery, I **would have traveled** around the world.',
        ],
      },
      {
        kind: 'note',
        text: 'As an alternative to WOULD, we can complete the second part of a third conditional sentence with COULD.',
      },
      {
        kind: 'examples',
        items: [
          'If **I’d stayed** at university, I **could have got** a master\'s degree.',
          'They **could have stayed** here if they **hadn’t found** any accommodation.',
        ],
      },
    ],
  },
  {
    slug: 'wishes-and-hopes',
    title: 'Talking About Wishes and Hopes',
    blocks: [
      {
        kind: 'p',
        text: '→ The verb wish usually describes the present or the past. Even though the grammar is in the past tense, the meaning is in the present.',
      },
      {
        kind: 'examples',
        items: [
          'I **wish** (that) I **had** more money now.',
          'She **wishes** (that) she **were** taller.',
          'He **wishes** (that) he **didn\'t have** to work today. → He has to work today.',
          'They **wish** (that) they **could come**. → They cannot come.',
          'I **wish** (that) it **wasn\'t raining**. → It is raining.',
        ],
      },
      {
        kind: 'p',
        text: '→ The verb hope usually describes a possible future situation. With the verb hope, we use the present simple tense even though we are talking about the future.',
      },
      {
        kind: 'examples',
        items: [
          'I have a test tomorrow. I **hope** (that) I **pass**.',
          'John is looking for a job. He **hopes** (that) he **gets** a job soon.',
          'It\'s cloudy. I **hope** (that) it **does not rain**.',
          'The movie starts soon. I **hope** (that) it **is** interesting.',
          'I **hope** I **travel** next year.',
        ],
      },
      {
        kind: 'p',
        text: '→ The expression would like usually describe desires. We use “I would like” to talk about something that we want or something that we want to do.',
      },
      {
        kind: 'examples',
        items: [
          'I **would like** some water, please. (=I want some water.)',
          '**I’d like** more information about this product.',
          'I **would like to be** rich.',
          'I **would like to have** curly hair.',
        ],
      },
    ],
  },
  {
    slug: 'past-modal-verbs',
    title: 'Past Modal Verbs',
    blocks: [
      {
        kind: 'p',
        text: 'In English, modal verbs are helping verbs, also known as auxiliary verbs, which indicate possibility, obligation, permission, ability, and deduction.',
      },
      {
        kind: 'p',
        text: 'A past modal verb implies something was possible, permitted, obligated, or able to be done in the hypothetical sense, but didn’t actually happen. These verbs are combined with “have” to create the past modal structure. Here is a list of past modal verbs:',
      },
      {
        kind: 'examples',
        items: [
          'She **could have eaten** the cake, but she chose to eat a salad instead.',
          'I **might have gone** to the concert if I didn’t have to work.',
          'Jan **should have stamped** the envelope before she mailed it.',
          'Fran **would have made** a great toast at the wedding, but she wasn’t there.',
        ],
      },
      { kind: 'h3', text: 'What Does Each Past Modal Verb Mean?' },
      { kind: 'h3', text: '1. Could have, may have, might have' },
      { kind: 'p', text: 'Indicates a past possibility or guess.' },
      {
        kind: 'examples',
        items: [
          'The boy **could have taken** the candy without paying, but he stopped at the register.',
          'Police think the suspect **may have left** the country using a fake passport.',
          'Why wasn’t Matt here yesterday? He **might have forgotten** about the meeting.',
        ],
      },
      { kind: 'h3', text: '2. Should have' },
      {
        kind: 'p',
        text: 'Indicates a past necessity or a regret. It shows that doing something was a good idea, or you were obligated to do it, but you didn’t, and you regret it.',
      },
      {
        kind: 'examples',
        items: [
          'Jon **should have bought** flowers for all of the ladies in the office, but the bouquets were really expensive.',
          'I **should have bought** you flowers, **should have given** you all my hours…',
        ],
      },
      { kind: 'h3', text: '3. Would have' },
      {
        kind: 'p',
        text: 'Indicates past volition or desire, meaning that the willingness to do something was there.',
      },
      {
        kind: 'examples',
        items: [
          'I **would have bought** a house by now if I had enough money.',
          'I **would have gone** to the party if I didn’t have any articles to write.',
        ],
      },
      { kind: 'h3', text: '4. Must have' },
      {
        kind: 'p',
        text: 'Indicates deduction or inference. You are inferring that something happened in a way because you have reasons to believe so.',
      },
      {
        kind: 'examples',
        items: [
          'The thief **must have had** a key. The door was locked and nothing was broken.',
          'Who told the newspapers about the prime minister\'s plans? It **must have been** someone close to him because only close people had access to his plans.',
        ],
      },
      { kind: 'h3', text: 'What about negative statements?' },
      { kind: 'h3', text: '1. Couldn’t have' },
      { kind: 'p', text: 'It wasn’t possible, no matter what (in the past).' },
      {
        kind: 'examples',
        items: ['I **couldn’t have gone** to the festival even if I had wanted to.'],
      },
      { kind: 'h3', text: '2. Can\'t have' },
      { kind: 'p', text: 'It isn\'t possible, no matter what (in the present).' },
      {
        kind: 'examples',
        items: ['She **can\'t have driven** there. Her car keys are still here.'],
      },
      { kind: 'h3', text: '3. Shouldn’t have' },
      { kind: 'p', text: 'It wasn’t a good idea to do it, but you still did it.' },
      {
        kind: 'examples',
        items: ['She **shouldn’t have eaten** that whole turkey. Now she’s out sick.'],
      },
      { kind: 'h3', text: '4. Wouldn’t have' },
      { kind: 'p', text: 'You didn’t want to do something, but did it anyway.' },
      {
        kind: 'examples',
        items: ['Jane **wouldn’t have gone** to the party if she had known Joe was there.'],
      },
    ],
  },
  {
    slug: 'active-passive-voice',
    title: 'Active and Passive Voice',
    blocks: [
      {
        kind: 'p',
        text: 'The passive voice is used to show interest in the person or object that experiences an action rather than the person or object that performs the action. In other words, the most important thing or person becomes the subject of the sentence.',
      },
      {
        kind: 'examples',
        items: [
          'The representatives **deviated** 1 billion reais in the Senate. → Active voice',
          '1 billion reais **were deviated** from the Senate. → Passive voice',
        ],
      },
      {
        kind: 'p',
        text: 'For this reason, when we use the passive voice the agent does not need to appear. The focus is on what happened instead of on who did it.',
      },
      {
        kind: 'examples',
        items: [
          'The passive voice **is used** frequently.',
          'Sometimes our efforts **are not fully appreciated**.',
        ],
      },
      { kind: 'h3', text: 'How do we form the passive voice?' },
      {
        kind: 'p',
        text: 'The passive voice in English is composed of two elements: BE in the same tense as the main verb in the active voice + past participle of the main verb (verb + -ed or 3rd column of irregular verbs.',
      },
      {
        kind: 'examples',
        items: [
          'She **delivers** the food. → The food **is delivered** (by her).',
          'Someone **built** the house in 1899. → The house **was built** in 1899 (by someone).',
          'Someone **is shipping** your package. → Your package **is being shipped** (by someone).',
          'Tomorrow, I **will eat** 2 hamburgers. → Tomorrow, 2 hamburgers **will be eaten** (by me).',
          'Spielberg **has directed** the movie ET. → The movie ET **has been directed** by Spielberg.',
        ],
      },
      { kind: 'h3', text: 'Step by step to form the passive voice' },
      { kind: 'p', text: 'Take the following sentence as an example:' },
      { kind: 'p', text: 'Someone built the house in 1899.' },
      {
        kind: 'list',
        items: [
          '1. Find the verb of the sentence.',
          '2. Find the subject of the sentence.',
          '3. Find the object of the verb.',
        ],
      },
      { kind: 'p', text: 'Someone built the house in 1899.' },
      {
        kind: 'list',
        items: [
          '4. Identify the verb tense of the main verb.',
          '5. Transform the verb into a compound form with the verb BE in the same tense as the original sentence + Participle form.',
        ],
      },
      {
        kind: 'list',
        items: [
          'Built → Simple Past',
          'Be in the Simple Past → Was/Were',
          'Participle of build → built (build/built/built)',
          'Compound form of built → was built',
        ],
      },
      {
        kind: 'list',
        items: [
          '6. The object of the original sentence in the active voice becomes the subject of the sentence in the passive voice.',
          '7. The subject of the original sentence in the active voice becomes the agent (followed by “by”) of the sentence in the passive voice.',
          '8. The complement remains the same.',
        ],
      },
      { kind: 'p', text: 'Result: The house was built in 1899.' },
    ],
  },
];
